/**
 * The app's icons: Lucide line icons (the Omni style), one glyph per meaning. Screens import from
 * here, never from lucide-react or @mui/icons-material, so a meaning keeps one look everywhere.
 *
 * Each glyph renders through MUI's SvgIcon, so the props screens already pass to icons keep
 * working: fontSize, sx, color and className, plus the theme's SvgIcon sizing. The test id follows
 * MUI's "<Name>Icon" convention.
 * @since NEXT_VERSION
 */
import { forwardRef } from "react";
import SvgIcon from "@mui/material/SvgIcon";
import {
  BadgeCheck,
  Bug,
  Calendar,
  CalendarCheck,
  ChartColumn,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsRight,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleMinus,
  CirclePlus,
  Clock,
  CloudUpload,
  Contrast,
  Copy,
  Database,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  EyeOff,
  FileCode,
  FileText,
  Flag,
  Folder,
  GraduationCap,
  Hourglass,
  IdCard,
  Inbox,
  Info,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  ListPlus,
  Lock,
  LogOut,
  Menu,
  Moon,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcwKey,
  Save,
  Search,
  Settings,
  ShieldCheck,
  Sun,
  Tag,
  Timer,
  Trash2,
  TriangleAlert,
  Undo2,
  User,
  Users,
  Wrench,
  X,
} from "lucide-react";

// Lucide draws at 2; 1.75 matches the lighter line of the Omni screens.
const STROKE_WIDTH = 1.75;

function fromLucide(Glyph, name) {
  const Icon = forwardRef(function LucideIcon({ sx, ...props }, ref) {
    const extraSx = Array.isArray(sx) ? sx : sx ? [sx] : [];
    return (
      <SvgIcon
        ref={ref}
        component={Glyph}
        inheritViewBox
        strokeWidth={STROKE_WIDTH}
        data-testid={`${name}Icon`}
        // SvgIcon fills with currentColor; Lucide glyphs are strokes. A caller's sx can still fill
        // one (a status dot).
        sx={[{ fill: "none" }, ...extraSx]}
        {...props}
      />
    );
  });
  Icon.displayName = `${name}Icon`;
  return Icon;
}

// Row actions
export const Edit = fromLucide(Pencil, "Edit");
export const ContentCopy = fromLucide(Copy, "ContentCopy");
export const Delete = fromLucide(Trash2, "Delete");
// Sends an approved activity back for review, so an undo arrow, not a close cross.
export const Disapprove = fromLucide(Undo2, "Disapprove");
// Info-only marker for rows with nothing to do (e.g. "Default approved").
export const NoAction = fromLucide(CircleMinus, "NoAction");
export const RowMenu = fromLucide(Ellipsis, "RowMenu");
export const Approve = fromLucide(Check, "Approve");
export const LockIcon = fromLucide(Lock, "Lock");

// Page and toolbar actions
export const Add = fromLucide(Plus, "Add");
export const AddMultiple = fromLucide(ListPlus, "AddMultiple");
export const AddCircle = fromLucide(CirclePlus, "AddCircle");
export const SearchIcon = fromLucide(Search, "Search");
export const Refresh = fromLucide(RefreshCw, "Refresh");
export const DownloadIcon = fromLucide(Download, "Download");
export const Upload = fromLucide(CloudUpload, "Upload");
export const OpenInNew = fromLucide(ExternalLink, "OpenInNew");
export const SaveIcon = fromLucide(Save, "Save");
export const Close = fromLucide(X, "Close");

// Status and feedback
export const InfoIcon = fromLucide(Info, "Info");
export const Warning = fromLucide(TriangleAlert, "Warning");
export const Done = fromLucide(CircleCheck, "Done");
export const Pending = fromLucide(Hourglass, "Pending");
export const Schedule = fromLucide(Clock, "Schedule");
export const PriorityHigh = fromLucide(CircleAlert, "PriorityHigh");
export const FlagIcon = fromLucide(Flag, "Flag");
export const StatusDot = fromLucide(Circle, "StatusDot");
export const Verified = fromLucide(BadgeCheck, "Verified");
export const VerifiedUser = fromLucide(ShieldCheck, "VerifiedUser");

// Navigation and disclosure
export const MenuIcon = fromLucide(Menu, "Menu");
export const ExpandMore = fromLucide(ChevronDown, "ExpandMore");
export const ExpandLess = fromLucide(ChevronUp, "ExpandLess");
export const ChevronLeftIcon = fromLucide(ChevronLeft, "ChevronLeft");
export const ChevronRightIcon = fromLucide(ChevronRight, "ChevronRight");
export const DoubleChevronRight = fromLucide(ChevronsRight, "DoubleChevronRight");

// Settings and display
export const SettingsIcon = fromLucide(Settings, "Settings");
export const LightMode = fromLucide(Sun, "LightMode");
export const DarkMode = fromLucide(Moon, "DarkMode");
export const ContrastIcon = fromLucide(Contrast, "Contrast");
export const Visibility = fromLucide(Eye, "Visibility");
export const VisibilityOff = fromLucide(EyeOff, "VisibilityOff");

// Things: people, time, files, modules
export const People = fromLucide(Users, "People");
export const Person = fromLucide(User, "Person");
export const CalendarIcon = fromLucide(Calendar, "Calendar");
export const EventAvailable = fromLucide(CalendarCheck, "EventAvailable");
export const Badge = fromLucide(IdCard, "Badge");
export const Report = fromLucide(ChartColumn, "Report");
export const Insights = fromLucide(ChartLine, "Insights");
export const Document = fromLucide(FileText, "Document");
export const CodeFile = fromLucide(FileCode, "CodeFile");
export const Label = fromLucide(Tag, "Label");
export const Build = fromLucide(Wrench, "Build");
export const BugReport = fromLucide(Bug, "BugReport");
export const Dashboard = fromLucide(LayoutDashboard, "Dashboard");
export const FolderIcon = fromLucide(Folder, "Folder");
export const InboxIcon = fromLucide(Inbox, "Inbox");
export const Storage = fromLucide(Database, "Storage");
export const School = fromLucide(GraduationCap, "School");
export const Logout = fromLucide(LogOut, "Logout");
export const Password = fromLucide(KeyRound, "Password");
export const LockReset = fromLucide(RotateCcwKey, "LockReset");
export const Timelapse = fromLucide(Timer, "Timelapse");
export const Checklist = fromLucide(ListChecks, "Checklist");
